import { Component, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseComponent } from 'app/components/base.component';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap';
import { ModalType } from 'app/components/modals/modal.const';
import { FormFieldType } from 'app/components/modals/modal-form-template/modal-form.const';
import {
  CRITERIA_OBJECT_TYPE,
  CRITERIA_TYPE,
  GetQCCriteriaQuery,
  QCCriteria,
  SmartQcService,
  ViewQCCriteria,
} from 'app/common/services/SmartQC.service';
import {
  FormFieldCode,
  buildCreateQCCriteriaModel,
  buildCreateQCCriteriaPayload,
  buildEditQCCriteriaModel,
  buildEditQCCriteriaPayload,
  buildTableColumns,
  buildViewQCCriteria,
} from './setting-criteria.util';
import { TableAction } from 'app/components/table-component/table.const';

type ModalAction = 'add' | 'edit' | 'close';

@Component({
  selector: 'app-smart-qc-setting-keyword',
  templateUrl: './setting-criteria.component.html',
  styleUrls: ['./setting-criteria.component.css'],
  providers: [SmartQcService],
})
export class SettingCriteriaComponent extends BaseComponent {
  @ViewChild('template') template: ElementRef;
  public loading: boolean = false;
  public qcCriterias: ViewQCCriteria[] = [];
  private _currentQCCriteria: ViewQCCriteria = null;
  public keyword: string = '';
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalPage: number = 0;
  public totalItems: number = 0;
  public modalAction: ModalAction = 'close';
  public tableColumns: any[] = [];

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected toastr: ToastrService,
    protected modalService: BsModalService,
    private smartQCService: SmartQcService
  ) {
    super(changeDetector, toastr, modalService);
  }

  ngOnInit(): void {
    this.tableColumns = buildTableColumns();
    this.getQCCriterias();
  }

  getQCCriterias() {
    this.loading = true;

    const query: GetQCCriteriaQuery = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    const name = this.keyword.trim();
    if (name) {
      query.name = name;
    }

    return this.smartQCService.getQCCriterias(query).subscribe(
      ({ data }) => {
        this.loading = false;
        this.qcCriterias = data.data.map((qcCriteria: QCCriteria) => buildViewQCCriteria(qcCriteria));
        this.totalItems = data.total;
        this.totalPage = Math.ceil(data.total / this.pageSize);
      },
      error => {
        this.loading = false;
        this.throwError(error.message);
      }
    );
  }

  initModal() {
    this.modelContent = {
      type: ModalType.Primary,
      title: this.modalAction === 'add' ? 'Thêm mới tiêu chí' : 'Cập nhật tiêu chí',
      submit_title: this.modalAction === 'add' ? 'Thêm mới' : 'Cập nhật',
      close_title: 'Hủy bỏ',
    };

    this.modelDisplay = [
      {
        key: FormFieldCode.CODE,
        label: 'Mã',
        placeholder: 'Mã tiêu chí',
        type: FormFieldType.Text,
        value: this.model[FormFieldCode.CODE],
        required: true,
        disabled: false,
        width: 'full',
        order: 0,
      },
      {
        key: FormFieldCode.NAME,
        label: 'Tên',
        placeholder: 'Tên tiêu chí',
        type: FormFieldType.Text,
        value: this.model[FormFieldCode.NAME],
        required: true,
        disabled: false,
        width: 'full',
        order: 1,
      },
      {
        key: FormFieldCode.DESCRIPTION,
        label: 'Mô tả',
        placeholder: 'Mô tả',
        type: FormFieldType.Textarea,
        value: this.model[FormFieldCode.DESCRIPTION],
        required: false,
        disabled: false,
        width: 'full',
        order: 2,
      },
      {
        key: FormFieldCode.TYPE,
        label: 'Loại',
        placeholder: 'Loại',
        type: FormFieldType.Radio,
        value: this.model[FormFieldCode.TYPE],
        required: true,
        disabled: false,
        width: 'full',
        order: 3,
        options: {
          data: [
            { id: CRITERIA_TYPE.AUTO, text: 'Auto' },
            { id: CRITERIA_TYPE.MANUAL, text: 'Manual' },
          ],
          style: {
            'justify-content': 'space-between',
          },
          checkInline: true,
        },
      },
      {
        key: FormFieldCode.OBJECT_TYPE,
        label: 'Đối tượng',
        placeholder: 'Đối tượng',
        type: FormFieldType.Radio,
        value: this.model[FormFieldCode.OBJECT_TYPE],
        required: true,
        disabled: false,
        width: 'full',
        order: 3,
        options: {
          data: [
            { id: CRITERIA_OBJECT_TYPE.AGENT, text: 'Agent' },
            { id: CRITERIA_OBJECT_TYPE.OTHER, text: 'Other' },
          ],
          style: {
            'justify-content': 'space-between',
          },
          checkInline: true,
        },
      },
    ];
  }

  handleSearch() {
    const keyword = this.keyword.trim();
    if (!keyword) {
      this.throwError('Nhập thông tin để tìm kiếm');
      return;
    }

    this.currentPage = 1;
    this.getQCCriterias();
  }

  handleCloseModal() {
    this.modalAction = 'close';
    this.closeModal();
  }

  handleOpenModal(modalAction: ModalAction) {
    this.modalAction = modalAction;
    this.model =
      modalAction === 'add' ? buildCreateQCCriteriaModel() : buildEditQCCriteriaModel(this._currentQCCriteria);

    this.initModal();

    this.openModal(
      this.template,
      {
        backdrop: true,
        ignoreBackdropClick: true,
        class: 'modal-xl',
        keyboard: false,
      },
      {},
      {
        modelContent: this.modelContent,
        modelDisplay: this.modelDisplay,
      }
    );
  }

  handleTableAction(event: TableAction, qcCriteria: ViewQCCriteria) {
    if (event === TableAction.edit) {
      this._currentQCCriteria = qcCriteria;
      this.handleOpenModal('edit');
    } else if (event === TableAction.delete) {
      const okDeleteQCCriteria = confirm('Bạn có muốn xóa tiêu chí này không?');
      if (okDeleteQCCriteria) {
        this.smartQCService.deleteQCCriteria(qcCriteria._id).subscribe(
          () => {
            this.throwSuccess('Xóa thành công');
            this.getQCCriterias();
          },
          error => {
            this.throwError(error.message);
          }
        );
      }
    }
  }

  async handleSubmit(model: any) {
    let observable: Observable<any>;

    if (this.modalAction === 'add') {
      observable = this.smartQCService.createQCCriteria(buildCreateQCCriteriaPayload(model));
    } else if (this.modalAction === 'edit') {
      observable = this.smartQCService.updateQCCriteria(
        this._currentQCCriteria._id,
        buildEditQCCriteriaPayload(model, this._currentQCCriteria)
      );
    }

    observable.subscribe(
      () => {
        const message = this.modalAction === 'add' ? 'Thêm mới thành công' : 'Cập nhật thành công';

        this.throwSuccess(message);
        this.getQCCriterias();
        this.handleCloseModal();
      },
      error => {
        this.message = {
          class: 'alert dark alert-danger',
          content: error.message,
        };

        setTimeout(() => {
          this.message = null;
        }, 3000);
      }
    );
  }

  async onChangeValueModel(_fieldCode: FormFieldCode, qcCriteria: any) {
    this.model = qcCriteria;
  }

  setPage(page: number) {
    let newPage: number = page;
    if (page < 1) {
      newPage = 1;
    }

    if (page > this.totalPage) {
      newPage = this.totalPage;
    }

    if (newPage !== this.currentPage) {
      this.currentPage = newPage;
      this.getQCCriterias();
    }
  }

  goPage2(page: string) {
    let toPage: any = page;
    if (!toPage) {
      toPage = 1;
    }

    if (Number.isInteger(toPage) && toPage <= 0) {
      toPage = 1;
    }

    this.setPage(toPage);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/admin/icontact/smart-qc/setting/criteria/setting-criteria.component.ts